import { supabase } from "@/integrations/supabase/client";
import type { ApplicationEntry, ApplicationStatus } from "@/context/ApplicationContext";

type ApplicationPatch = Partial<ApplicationEntry> & { notes?: string | null };

export const applicationsQueryKey = (userId: string) => ["applications", userId] as const;

function rowToEntry(row: any): ApplicationEntry {
  return {
    id: row.id,
    company: row.company,
    role: row.role_title,
    status: row.status as ApplicationStatus,
    matchScore: row.match_score ?? null,
    rejectionReason: row.rejection_reason ?? null,
    appliedAt: (row.applied_at as string)?.slice(0, 10) ?? "",
    jdId: row.jd_id ?? null,
  };
}

function entryToInsert(userId: string, entry: ApplicationEntry) {
  return {
    id: entry.id,
    user_id: userId,
    company: entry.company,
    role_title: entry.role,
    status: entry.status,
    match_score: entry.matchScore,
    rejection_reason: entry.rejectionReason,
    applied_at: entry.appliedAt ? new Date(entry.appliedAt).toISOString() : new Date().toISOString(),
    jd_id: entry.jdId ?? null,
  };
}

export async function fetchApplications(userId: string): Promise<ApplicationEntry[]> {
  const { data, error } = await supabase
    .from("application_tracking")
    .select("*")
    .eq("user_id", userId)
    .order("applied_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(rowToEntry);
}

export async function insertApplication(userId: string, entry: ApplicationEntry) {
  const { error } = await supabase
    .from("application_tracking")
    .insert(entryToInsert(userId, entry));
  if (error) throw error;
}

export async function updateApplication(
  userId: string,
  id: string,
  patch: ApplicationPatch,
) {
  const dbPatch: Record<string, unknown> = {};
  if (patch.company !== undefined) dbPatch.company = patch.company;
  if (patch.role !== undefined) dbPatch.role_title = patch.role;
  if (patch.status !== undefined) dbPatch.status = patch.status;
  if (patch.matchScore !== undefined) dbPatch.match_score = patch.matchScore;
  if (patch.rejectionReason !== undefined) dbPatch.rejection_reason = patch.rejectionReason;
  if (patch.appliedAt !== undefined)
    dbPatch.applied_at = new Date(patch.appliedAt).toISOString();
  if (patch.jdId !== undefined) dbPatch.jd_id = patch.jdId;
  if (patch.notes !== undefined) dbPatch.notes = patch.notes;

  const { error } = await supabase
    .from("application_tracking")
    .update(dbPatch as never)
    .eq("id", id)
    .eq("user_id", userId);
  if (error) throw error;
}

export async function deleteApplication(userId: string, id: string) {
  const { error } = await supabase
    .from("application_tracking")
    .delete()
    .eq("id", id)
    .eq("user_id", userId);
  if (error) throw error;
}

export async function replaceAllApplications(userId: string, rows: ApplicationEntry[]) {
  // Wipe and bulk-insert
  const { error: delErr } = await supabase
    .from("application_tracking")
    .delete()
    .eq("user_id", userId);
  if (delErr) throw delErr;
  if (rows.length === 0) return;
  const { error } = await supabase
    .from("application_tracking")
    .insert(rows.map((r) => entryToInsert(userId, r)));
  if (error) throw error;
}
